import type {
    ResponseWithHeaders,
    MetaCompleteRegisterCredentialResponse,
    MetaChallengeResponse,
    CompleteRegisterCredentialRequest,
} from 'remotes/@api/authorization_v4';
import { ServiceResponseData } from 'service/Authorization/infoMethod';

const { ExtendedAuthenticatorCredentialEndpointApi } = await import(
    'remotes/@api/authorization_v4'
);
const { globalStore } = await import('remotes/@global-store/GlobalStore');
const api = new ExtendedAuthenticatorCredentialEndpointApi();

/**
 * @description BiometricService.
 */
export class BiometricService {
    /**
     * @description GetChallenge.
     * @returns {Promise} - Return of challenge Method.
     */
    public static async getChallenge(): Promise<
        ResponseWithHeaders<MetaChallengeResponse>
    > {
        return api.createChallengeUsingPOST();
    }

    /**
     * @description Initial.
     * @returns {Promise} - Return of initial Method.
     */
    public static async initial(): Promise<
        ServiceResponseData<PublicKeyCredentialCreationOptions | null>
    > {
        const { data, result, headers } =
            await api.initialCredentialRegistrationUsingPOST();

        if (data) {
            const userId = `${String(data?.user?.id)}${
                globalStore.deviseInfo.browser.name
            }${
                window.matchMedia('(display-mode: standalone)').matches
                    ? 'pwa'
                    : ''
            }`;
            const userName = `${String(data?.user?.displayName)} (${
                globalStore.deviseInfo.browser.name
            }${
                window.matchMedia('(display-mode: standalone)').matches
                    ? ' pwa'
                    : ''
            })`;
            const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions =
                {
                    challenge: Uint8Array.from(String(data?.challenge), (c) =>
                        c.charCodeAt(0),
                    ),
                    rp: {
                        name: String(data?.relyingParty?.name),
                        id: String(data?.relyingParty?.id),
                    },
                    user: {
                        name: userName,
                        displayName: userName,
                        id: Uint8Array.from(userId, (c) => c.charCodeAt(0)),
                    },
                    pubKeyCredParams: data?.pubKeyCredParams
                        ? ([
                              data.pubKeyCredParams,
                          ] as PublicKeyCredentialParameters[])
                        : [],
                    authenticatorSelection: {
                        authenticatorAttachment:
                            data?.authenticatorSelection
                                ?.authenticatorAttachment,
                        userVerification:
                            data?.authenticatorSelection?.userVerification,
                    },
                    timeout: data?.timeout,
                    attestation: data?.attestation,
                };
            return {
                data: publicKeyCredentialCreationOptions,
                result,
                headers,
            };
        }

        return {
            data: null,
            result,
            headers,
        };
    }

    /**
     * @param {CompleteRegisterCredentialRequest} request - Request.
     * @description Register.
     * @returns {Promise} - Return of register Method.
     */
    public static async register(
        request: CompleteRegisterCredentialRequest,
    ): Promise<ResponseWithHeaders<MetaCompleteRegisterCredentialResponse>> {
        return api.registerNewCredentialsUsingPATCH({ request });
    }
}
